import React from "react";
import S from "./Youtube.styled";
import { IWPVideo } from "@/src/types";
import { getImage } from "gatsby-plugin-image";
import PlayIcon from "@/assets/play.svg";

export interface IYoutubeProps {
  video: IWPVideo;
  aspectRatio?: boolean;
}

const Youtube: React.FC<IYoutubeProps> = ({
  video: { youtubeLink, image },
  aspectRatio = true,
}) => {
  const [youtubeId, setYoutubeId] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (youtubeLink) {
      try {
        const url = new URL(youtubeLink);

        // For standard YouTube links (with "v=" in query params)
        if (url.hostname.includes("youtube.com")) {
          const searchParams = new URLSearchParams(url.search);
          const id = searchParams.get("v");
          setYoutubeId(id);
        }
        // For shortened YouTube links (youtu.be)
        else if (url.hostname.includes("youtu.be")) {
          const id = url.pathname.split("/")[1];
          setYoutubeId(id);
        }
      } catch (error) {
        console.error("Invalid YouTube URL:", error);
        setYoutubeId(null); // Set to null if the URL is invalid
      }
    }
  }, [youtubeLink]);

  const imageData = image?.localFile?.childImageSharp?.gatsbyImageData
    ? getImage(image.localFile.childImageSharp.gatsbyImageData)
    : null;

  const iframeRef = React.useRef<HTMLIFrameElement>(null);
  const [playing, setPlaying] = React.useState(false);

  //on click play video
  const onClick = () => {
    if (iframeRef.current) {
      iframeRef.current.src += "?autoplay=1";
      setPlaying(true);
    }
  };

  React.useEffect(() => {
    //on new render in slideshow, reset playing state
    setPlaying(false);
  }, [youtubeId]);

  return (
    <S.Container onClick={onClick} $playing={playing}>
      {imageData && (
        <S.Image
          image={imageData}
          alt={image?.altText || ""}
          objectFit="cover"
          $aspectRatio={aspectRatio}
        />
      )}
      <PlayIcon />
      <S.Iframe
        $aspectRatio={aspectRatio}
        ref={iframeRef}
        src={`https://www.youtube.com/embed/${youtubeId}`}
        title="YouTube video player"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        referrerPolicy="strict-origin-when-cross-origin"
        allowFullScreen
      />
    </S.Container>
  );
};

export default Youtube;
